import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'

export default function MountMuhavuraClimb() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Mount Muhavura Volcano Climb
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Summit Uganda's third-highest peak at 4,127 meters
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(41 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-mountain" className="w-4 h-4 mr-2" />
                4,127m Summit
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-eye" className="w-4 h-4 mr-2" />
                8-Volcano Views
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-person-hiking" className="w-4 h-4 mr-2" />
                Strenuous Hike
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Summit
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Uganda's Ultimate One-Day Mountain Challenge
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Mount Muhavura, meaning "the guide" in the local Kinyarwanda language, towers over Mgahinga at 4,127 meters, making it Uganda's third-highest peak. Unlike multi-day technical climbs, Muhavura can be summited in a single strenuous day, offering determined hikers the chance to stand above 4,000 meters and experience genuine high-altitude mountain conditions without expedition-level commitment.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The eight to ten-hour round trip is relentlessly steep, gaining over 1,700 vertical meters through five distinct vegetation zones before emerging onto a rocky summit with possibly the finest panoramic views in East Africa. On supremely clear days, you can count eight Virunga volcanoes marching across the horizon like ancient sentinels—a sight found nowhere else.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The climb begins through cultivated fields before plunging into bamboo forest. Beyond the tree line, the landscape transforms dramatically into the Afromontane heath zone, where giant lobelias and groundsels dominate. The final section involves scrambling over volcanic boulders and loose scree to reach the crater lake at the summit.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Summit Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-mountain', title: '4,127m Elevation', description: "Uganda's 3rd highest peak—genuine high-altitude experience" },
                  { icon: 'fa-eye', title: '8-Volcano Panorama', description: 'See entire Virunga chain on clear days—unmatched views' },
                  { icon: 'fa-trophy', title: 'Peak-Bagging Prestige', description: "Join elite group who summit one of East Africa's toughest day climbs" },
                  { icon: 'fa-layer-group', title: '5 Vegetation Zones', description: 'Bamboo, hagenia, heathland, moorland, alpine desert transition' },
                  { icon: 'fa-flag', title: '3-Country Views', description: 'See Uganda, Rwanda, and DRC from summit—geographical perspective' },
                  { icon: 'fa-person-running', title: 'Ultimate Fitness Test', description: '8-10 hours, 1,700m gain—genuinely strenuous challenge' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Climb Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and fitness level. Timing depends on weather conditions and group pace.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '07:00', title: 'Early Start', desc: 'Briefing and begin—earlier start than other volcanoes due to length.' },
                  { time: '09:00', title: 'Bamboo to Hagenia', desc: 'Steep climbing through vegetation zones, golden monkey habitat.' },
                  { time: '11:00', title: 'Tree Line Exit', desc: 'Emerge into Afromontane heathland, giant lobelias appear.' },
                  { time: '12:30', title: 'Alpine Zone', desc: 'Rocky slopes, thin air, frequent breaks as altitude effects increase.' },
                  { time: '14:00', title: 'Summit 4,127m!', desc: 'Reach peak, 8-volcano views, lunch and photos on rocky summit.' },
                  { time: '14:45', title: 'Careful Descent', desc: 'Navigate scree and loose rocks, poles essential for balance.' },
                  { time: '17:30', title: 'Return', desc: 'Arrive base exhausted, receive certificate, rest!' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Gear
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof hiking boots (essential)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Rain jacket and warm layers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Gloves (it gets cold at the top)</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                    Equipment
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Daypack with rain cover</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>3-4 liters of water</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Energy snacks and lunch</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Critical Information
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'How fit do I need to be?', a: "Excellent fitness essential. If you don't regularly hike, run, or do cardio, this will be extremely difficult. Prepare with training hikes and cardio workouts for weeks before." },
                  { q: 'Will I get altitude sickness?', a: 'Possible at 4,127m. Mild symptoms (headache, nausea) common. Slow pace and hydration help. Those with altitude sensitivity history should consult doctors.' },
                  { q: 'What if weather is bad?', a: 'Rangers may cancel for safety—rain makes trail treacherous and eliminates views. Dry season (June-Sept, Dec-Feb) offers best odds.' },
                  { q: 'Porter necessary?', a: 'Strongly recommended. Carrying even a daypack at altitude drains energy  you need for climbing. Porters also provide physical assistance when exhausted. Hiring them supports local communities.' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Mgahinga Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/mount-sabinyo-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800"
                      alt="Mount Sabinyo"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Sabinyo Climb</h3>
                    <p className="text-gray-700 mb-4">Summit three countries at once.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/mount-gahinga-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800"
                      alt="Mount Gahinga"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Gahinga Climb</h3>
                    <p className="text-gray-700 mb-4">Easier climb with crater swamp.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 8-10 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Dry Seasons</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Very Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 18+</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Summit
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Park entrance fees",
                    "Ranger guide",
                    "Summit certificate",
                    "Security escort",
                    "Emergency support"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Porter (highly recommended)",
                    "Transport to park",
                    "Meals and water",
                    "Personal gear"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Altitude:</strong> Risk of sickness.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> High level required.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Mount Muhavura Climb"
      />
    </div>
  )
}
